/**
 * PortfolioDetail — Executive dashboard for a single portfolio.
 *
 * Sections shipped:
 *   - Header + AI Executive Summary (Claude-drafted headline)
 *   - Delivery health (project status buckets + at-risk breakdown)
 *   - Tasks & Milestones summary
 *   - Financial (permission-gated; hidden when scrubbed)
 *   - Utilization (last 7 days) + top contributors
 *   - Top risks (log/RAID entries)
 *   - Client pulse (CSAT + retention)
 *   - Project drill-down table
 *   - Configuration: PDF sections, digest recipients, send-now, PDF download
 */
import { useEffect, useState, useMemo, Fragment } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  PieChart, Download, Send, Sparkles, RefreshCw, Settings2,
  TrendingUp, TrendingDown, AlertTriangle, Users as UsersIcon,
  FolderKanban, Clock, Star, ArrowUpRight, Wallet,
  MessageSquarePlus, ChevronDown, ChevronRight, Plus, ListTodo, Activity,
  Maximize2, Minimize2, StickyNote,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  CombinedTasksView, CombinedMilestonesView, CombinedLogView,
  CombinedGanttView, TaskPeekPanel, QuickCommentDialog,
} from "@/components/portfolio/PortfolioCombinedViews";
import { QuickAddTaskPopover } from "@/components/tasks/QuickAddTask";
import Tasks from "@/pages/app/Tasks";
import ProjectUpdatesPeekDialog from "@/components/portfolio/ProjectUpdatesPeekDialog";
import BoardNotesTab from "@/components/portfolio/BoardNotesTab";
import PortfolioTrendsPanel from "@/components/portfolio/PortfolioTrendsPanel";
import AdvancedTable from "@/components/table/AdvancedTable";

const HEALTH_LABEL = {
  on_track: "On track", at_risk: "At risk",
  off_track: "Off track", blocked: "Blocked", unset: "Unset",
};
const HEALTH_COLOR = {
  on_track: "text-emerald-700 bg-emerald-50 border-emerald-200",
  at_risk: "text-amber-700 bg-amber-50 border-amber-200",
  off_track: "text-rose-700 bg-rose-50 border-rose-200",
  blocked: "text-rose-800 bg-rose-100 border-rose-300",
  unset: "text-slate-600 bg-slate-50 border-slate-200",
};

const ALL_SECTIONS = [
  { key: "cover", label: "Cover page" },
  { key: "executive_summary", label: "Executive summary (AI)" },
  { key: "delivery_health", label: "Delivery health" },
  { key: "financial", label: "Financial rollup" },
  { key: "milestones", label: "Milestones" },
  { key: "team_utilization", label: "Team utilization" },
  { key: "top_risks", label: "Top risks & issues" },
  { key: "nps_pulse", label: "Client pulse (NPS)" },
];

export default function PortfolioDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [cfg, setCfg] = useState({
    pdf_sections: [], digest_recipients: "",
    digest_enabled: false, digest_cadence: "weekly",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        api.get(`/portfolios/${pid}`),
        api.get(`/portfolios/${pid}/rollup`),
      ]);
      setPortfolio(pRes.data);
      setRollup(rRes.data);
      setCfg({
        pdf_sections: pRes.data.pdf_sections || ALL_SECTIONS.map((s) => s.key),
        digest_recipients: (pRes.data.digest_recipients || []).join(", "),
        digest_enabled: !!pRes.data.digest_enabled,
        digest_cadence: pRes.data.digest_cadence || "weekly",
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load portfolio"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pid]); // eslint-disable-line

  const runAiSummary = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/portfolios/${pid}/ai-summary`);
      setPortfolio((p) => ({ ...p, last_ai_summary: data.summary,
                              last_ai_summary_at: new Date().toISOString() }));
      toast.success("Executive summary refreshed");
    } catch (e) { toast.error(apiErrorMessage(e, "AI summary failed")); }
    finally { setAiBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/portfolios/${pid}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `portfolio-${(portfolio?.name || "report").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "PDF export failed")); }
  };

  const sendDigest = async () => {
    if (!(portfolio.digest_recipients || []).length) {
      toast.error("Add digest recipients first (open Config)");
      return;
    }
    setDigestBusy(true);
    try {
      const { data } = await api.post(`/portfolios/${pid}/send-digest`);
      toast.success(`Digest sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Send digest failed")); }
    finally { setDigestBusy(false); }
  };

  const saveConfig = async () => {
    try {
      await api.patch(`/portfolios/${pid}`, {
        pdf_sections: cfg.pdf_sections,
        digest_recipients: cfg.digest_recipients
          ? cfg.digest_recipients.split(",").map((r) => r.trim()).filter(Boolean)
          : [],
        digest_enabled: cfg.digest_enabled,
        digest_cadence: cfg.digest_cadence,
      });
      toast.success("Config saved");
      setShowConfig(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save")); }
  };

  const toggleSection = (key) => {
    setCfg((prev) => ({
      ...prev,
      pdf_sections: prev.pdf_sections.includes(key)
        ? prev.pdf_sections.filter((k) => k !== key)
        : [...prev.pdf_sections, key],
    }));
  };

  const atRiskCount = useMemo(() => {
    if (!rollup) return 0;
    const h = rollup.health_buckets || {};
    return (h.at_risk || 0) + (h.off_track || 0) + (h.blocked || 0);
  }, [rollup]);

  if (loading || !portfolio || !rollup) {
    return <div className="p-10 text-sm text-muted-foreground">Loading portfolio…</div>;
  }

  return (
    <>
      <PageHeader
        eyebrow="Executive Portfolio"
        title={portfolio.name}
        description={portfolio.description || `Rollup across ${rollup.projects_count} project(s)`}
        testid="portfolio-detail-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-none"
                    onClick={runAiSummary} disabled={aiBusy}
                    data-testid="portfolio-ai-summary">
              <Sparkles className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">{aiBusy ? "Thinking…" : "Refresh AI summary"}</span>
            </Button>
            <Button size="sm" variant="outline" className="rounded-none"
                    onClick={downloadPdf}
                    data-testid="portfolio-pdf">
              <Download className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button size="sm" variant="outline" className="rounded-none"
                    onClick={sendDigest} disabled={digestBusy}
                    data-testid="portfolio-send-digest">
              <Send className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">{digestBusy ? "Sending…" : "Send digest"}</span>
            </Button>
            <Button size="sm" variant="ghost" className="rounded-none"
                    onClick={() => setShowConfig(true)}
                    data-testid="portfolio-config-btn">
              <Settings2 className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Config</span>
            </Button>
          </div>
        }
      />

      <div className="px-3 sm:px-6 md:px-10 py-4 sm:py-6 space-y-4">
        {/* AI Executive Summary card — always visible; the board headline
            is the whole point of this page. */}
        <div className="border border-border p-4 sm:p-6 bg-gradient-to-br from-indigo-50 to-transparent" data-testid="portfolio-ai-card">
          <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Board headline
          </div>
          <p className="text-sm sm:text-lg leading-relaxed" data-testid="portfolio-ai-text">
            {portfolio.last_ai_summary
              || "No AI summary yet. Click 'Refresh AI summary' to generate a board-ready headline."}
          </p>
          {portfolio.last_ai_summary_at && (
            <div className="text-[10px] text-muted-foreground mt-2">
              Updated {fmtRelative(portfolio.last_ai_summary_at)}
            </div>
          )}
        </div>

        {/* KPI strip — compact, always visible */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="portfolio-kpis">
          <Kpi label="Projects" value={rollup.projects_count}
               icon={FolderKanban} testid="kpi-projects" />
          <Kpi label="On track" value={rollup.health_buckets?.on_track || 0}
               icon={TrendingUp} tone="emerald" testid="kpi-on-track" />
          <Kpi label="At risk" value={atRiskCount}
               icon={AlertTriangle} tone="amber" testid="kpi-at-risk" />
          <Kpi label="Task completion"
               value={`${rollup.tasks_summary?.completion_pct || 0}%`}
               icon={TrendingUp} testid="kpi-completion" />
          <Kpi label="Overdue tasks" value={rollup.tasks_summary?.overdue || 0}
               icon={TrendingDown} tone="rose" testid="kpi-overdue" />
          <Kpi label="Hours logged (7d)"
               value={rollup.utilization?.total_hours_7d || 0}
               icon={Clock} testid="kpi-hours" />
        </div>

        {/* R138w · Feb 2026 — 8-week trend sparklines panel. */}
        <CollapsibleSection storageKey={`pf-trends-${pid}`}
                                     title="8-week velocity trends"
                                     eyebrow={<><TrendingUp className="w-3 h-3" /> Sparklines</>}
                                     testid="portfolio-trends-section">
          <PortfolioTrendsPanel portfolioId={pid} />
        </CollapsibleSection>

        {/* Financial rollup — collapsible */}
        {!rollup.financial?.restricted && (
          <CollapsibleSection storageKey={`pf-fin-${pid}`}
                                       title="Financial rollup"
                                       eyebrow={<><Wallet className="w-3 h-3" /> Board eyes only</>}
                                       testid="portfolio-financial">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              <FinCell label="Budget" value={rollup.financial.budget_total} />
              <FinCell label="Invoiced" value={rollup.financial.invoiced} />
              <FinCell label="Paid" value={rollup.financial.paid} />
              <FinCell label="Outstanding" value={rollup.financial.outstanding} tone="rose" />
              <FinCell label="Collected %" value={`${rollup.financial.collected_pct || 0}%`} />
            </div>
          </CollapsibleSection>
        )}

        {/* At-risk + Top risks — two-column collapsible */}
        <CollapsibleSection storageKey={`pf-risks-${pid}`}
                                     title="At-risk projects & top risks"
                                     eyebrow={<><AlertTriangle className="w-3 h-3" /> Watch list</>}
                                     testid="portfolio-risks-section">
          <div className="grid md:grid-cols-2 gap-4">
            <div data-testid="portfolio-at-risk">
              <div className="label-eyebrow text-[10px] mb-3">At-risk projects</div>
              {rollup.at_risk_projects?.length === 0 ? (
                <div className="text-sm text-muted-foreground">All projects are on track. 🎉</div>
              ) : (
                <div className="space-y-2">
                  {rollup.at_risk_projects.slice(0, 8).map((p) => (
                    <Link key={p.id} to={`/app/projects/${p.id}`}
                          className="flex items-center justify-between border border-border p-2 hover:border-primary/40 transition"
                          data-testid={`portfolio-atrisk-${p.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{p.name}</div>
                        {p.client_name && (
                          <div className="text-[11px] text-muted-foreground truncate">{p.client_name}</div>
                        )}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 border ${HEALTH_COLOR[p.health] || ""}`}>
                        {HEALTH_LABEL[p.health] || p.health}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div data-testid="portfolio-top-risks">
              <div className="label-eyebrow text-[10px] mb-3">Top risks & issues</div>
              {rollup.top_risks?.length === 0 ? (
                <div className="text-sm text-muted-foreground">No open risks or issues in the last 30 days.</div>
              ) : (
                <div className="space-y-2">
                  {rollup.top_risks.slice(0, 8).map((r) => (
                    <div key={r.id} className="flex items-start justify-between border border-border p-2"
                         data-testid={`portfolio-risk-${r.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{r.title}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {r.kind} · {fmtRelative(r.created_at)}
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-rose-700 font-bold">
                        {r.severity || "med"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CollapsibleSection>

        {/* Utilization + Client pulse — two-column collapsible */}
        <CollapsibleSection storageKey={`pf-people-${pid}`}
                                     title="People & client pulse"
                                     eyebrow={<><UsersIcon className="w-3 h-3" /> Team & CSAT</>}
                                     testid="portfolio-people-section">
          <div className="grid md:grid-cols-2 gap-4">
            <div data-testid="portfolio-util">
              <div className="label-eyebrow text-[10px] mb-3">Top contributors (last 7 days)</div>
              {rollup.utilization?.top_contributors?.length === 0 ? (
                <div className="text-sm text-muted-foreground">No timesheet activity in the last 7 days.</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {rollup.utilization.top_contributors.slice(0, 8).map((c) => (
                      <tr key={c.user_id} className="border-b border-border">
                        <td className="py-1.5">
                          <div className="flex items-center gap-2">
                            <UsersIcon className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>{c.user_name || "—"}</span>
                          </div>
                        </td>
                        <td className="py-1.5 text-right font-medium tabular-nums">
                          {c.hours.toFixed(1)}h
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div data-testid="portfolio-nps">
              <div className="label-eyebrow text-[10px] mb-3">
                Client pulse · Avg CSAT{" "}
                <span className="text-emerald-700 font-bold">
                  {rollup.nps_pulse?.avg_csat != null ? `★ ${rollup.nps_pulse.avg_csat}` : "N/A"}
                </span>
              </div>
              {rollup.nps_pulse?.clients?.length === 0 ? (
                <div className="text-sm text-muted-foreground">No clients in this portfolio.</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {rollup.nps_pulse.clients.slice(0, 8).map((c) => (
                      <tr key={c.id} className="border-b border-border">
                        <td className="py-1.5 truncate max-w-[150px]">{c.name || "—"}</td>
                        <td className="py-1.5 text-center">
                          {c.csat_score != null ? (
                            <span className="flex items-center gap-1 justify-center">
                              <Star className="w-3 h-3 text-amber-500" />
                              {c.csat_score}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="py-1.5 text-right text-xs text-muted-foreground">
                          {c.retention_risk ? c.retention_risk : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </CollapsibleSection>

        {/* R138u · Executive tabs — Projects (AdvancedTable) + 4 combined
            views + Board Notes. Every tab body is wrapped in a
            FullscreenTab so directors can expand for a full-screen review. */}
        <Tabs defaultValue="projects" className="w-full" data-testid="portfolio-tabs">
          <TabsList className="rounded-none w-full justify-start overflow-x-auto flex-nowrap no-scrollbar" data-testid="portfolio-tabs-list">
            <TabsTrigger value="projects" className="rounded-none flex-shrink-0" data-testid="portfolio-tab-projects"><FolderKanban className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline"> Projects</span></TabsTrigger>
            <TabsTrigger value="tasks" className="rounded-none flex-shrink-0 text-xs sm:text-sm" data-testid="portfolio-tab-tasks">Tasks</TabsTrigger>
            <TabsTrigger value="milestones" className="rounded-none flex-shrink-0 text-xs sm:text-sm" data-testid="portfolio-tab-milestones">Milestones</TabsTrigger>
            <TabsTrigger value="gantt" className="rounded-none flex-shrink-0 text-xs sm:text-sm" data-testid="portfolio-tab-gantt">Gantt</TabsTrigger>
            <TabsTrigger value="notes" className="rounded-none flex-shrink-0" data-testid="portfolio-tab-notes"><StickyNote className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline"> Board Notes</span></TabsTrigger>
            <TabsTrigger value="log" className="rounded-none flex-shrink-0 text-xs sm:text-sm" data-testid="portfolio-tab-log">Log</TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="pt-4">
            <FullscreenTab title={`Projects · ${portfolio.name}`}>
              <ProjectsAdvancedTable
                portfolioId={pid}
                projects={rollup.projects}
                onCommentPosted={load}
              />
            </FullscreenTab>
          </TabsContent>
          <TabsContent value="tasks" className="pt-4">
            <FullscreenTab title={`Combined tasks · ${portfolio.name}`}>
              <CombinedTasksView portfolioId={pid} />
            </FullscreenTab>
          </TabsContent>
          <TabsContent value="milestones" className="pt-4">
            <FullscreenTab title={`Combined milestones · ${portfolio.name}`}>
              <CombinedMilestonesView portfolioId={pid} />
            </FullscreenTab>
          </TabsContent>
          <TabsContent value="gantt" className="pt-4">
            <FullscreenTab title={`Gantt · ${portfolio.name}`}>
              <CombinedGanttView portfolioId={pid} />
            </FullscreenTab>
          </TabsContent>
          <TabsContent value="notes" className="pt-4">
            <FullscreenTab title={`Board notes · ${portfolio.name}`}>
              <BoardNotesTab portfolioId={pid} />
            </FullscreenTab>
          </TabsContent>
          <TabsContent value="log" className="pt-4">
            <FullscreenTab title={`Activity log · ${portfolio.name}`}>
              <CombinedLogView portfolioId={pid} />
            </FullscreenTab>
          </TabsContent>
        </Tabs>
      </div>

      {/* Config dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-2xl" data-testid="portfolio-config-dialog">
          <DialogHeader>
            <DialogTitle>Configure portfolio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">
                PDF sections to include
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_SECTIONS.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer border border-border p-2">
                    <input type="checkbox"
                           checked={cfg.pdf_sections.includes(s.key)}
                           onChange={() => toggleSection(s.key)}
                           data-testid={`portfolio-cfg-section-${s.key}`} />
                    <span>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">
                Weekly digest recipients
              </Label>
              <Input
                value={cfg.digest_recipients}
                onChange={(e) => setCfg({ ...cfg, digest_recipients: e.target.value })}
                placeholder="board@company.com, ceo@company.com"
                data-testid="portfolio-cfg-recipients" />
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-border">
              <Switch checked={cfg.digest_enabled}
                      onCheckedChange={(v) => setCfg({ ...cfg, digest_enabled: v })}
                      data-testid="portfolio-cfg-enabled" />
              <Label className="text-sm">Enable scheduled digest</Label>
              <select
                value={cfg.digest_cadence}
                onChange={(e) => setCfg({ ...cfg, digest_cadence: e.target.value })}
                className="ml-auto h-8 border border-border bg-background text-xs px-2"
                data-testid="portfolio-cfg-cadence">
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowConfig(false)}>Cancel</Button>
              <Button onClick={saveConfig} data-testid="portfolio-cfg-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Kpi({ label, value, icon: Icon, tone, testid }) {
  const toneClass = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  }[tone] || "text-foreground";
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}


// R138u · Collapsible dashboard section — persists open/closed state to
// localStorage so directors don't re-collapse their least-used sections
// on every visit.
function CollapsibleSection({ storageKey, title, eyebrow, testid, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(`pf-section:${storageKey}`);
      if (raw === "0") return false;
      if (raw === "1") return true;
    } catch { /* ignore */ }
    return defaultOpen;
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(`pf-section:${storageKey}`, next ? "1" : "0"); } catch { /* ignore */ }
  };
  return (
    <div className="border border-border" data-testid={testid}>
      <button type="button" onClick={toggle}
                className="w-full flex items-center justify-between p-2 sm:p-3 hover:bg-muted/30 transition-colors"
                data-testid={`${testid}-toggle`}>
        <div className="flex items-center gap-2 sm:gap-3">
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="text-xs sm:text-sm font-semibold">{title}</span>
        </div>
        {eyebrow && (
          <span className="hidden sm:flex text-[10px] uppercase tracking-widest text-muted-foreground items-center gap-1.5">
            {eyebrow}
          </span>
        )}
      </button>
      {open && <div className="p-3 sm:p-5 pt-2 border-t border-border">{children}</div>}
    </div>
  );
}


// R138u · Full-screen wrapper for tab content — one click expands the
// tab body to a full-viewport overlay so directors reviewing the details
// of a project/tasks/gantt aren't cramped by the header + KPI strip.
function FullscreenTab({ title, children }) {
  const [full, setFull] = useState(false);
  // R138w · Feb 2026 — ESC exits fullscreen; body scroll locked while active.
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    document.body.classList.add("overflow-hidden");
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [full]);
  const inner = (
    <>
      <div className="flex items-center justify-end mb-2">
        <Button size="sm" variant="ghost" className="rounded-none h-7"
                  onClick={() => setFull((v) => !v)}
                  data-testid="portfolio-fullscreen-toggle">
          {full ? <><Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Exit fullscreen</> : <><Maximize2 className="w-3.5 h-3.5 mr-1.5" /> Fullscreen</>}
        </Button>
      </div>
      {children}
    </>
  );
  if (!full) return <div>{inner}</div>;
  return (
    <div className="fixed inset-0 z-[70] bg-background overflow-auto" data-testid="portfolio-fullscreen-overlay">
      <div className="sticky top-0 z-10 bg-background border-b border-border px-6 py-3 flex items-center justify-between shadow-md">
        <div className="text-sm font-semibold">{title}</div>
        <Button size="sm" variant="outline" className="rounded-none h-7"
                  onClick={() => setFull(false)}
                  data-testid="portfolio-fullscreen-exit">
          <Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Exit fullscreen
        </Button>
      </div>
      <div className="px-6 py-4">{children}</div>
    </div>
  );
}


// R138x · Feb 2026 — Full Tasks powerhouse popup scoped to one project.
// Mirrors PersonTasksDialog in TaskIntelligence — mounts the entire
// <Tasks /> module so the director gets the same list/cards/kanban/
// calendar views, bulk edit, quick-add, and drill-into-detail popup
// without leaving the portfolio.
function ProjectTasksDrillDialog({ project, onClose }) {
  if (!project) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-[96vw] w-[96vw] h-[92vh] p-0 gap-0 overflow-hidden flex flex-col"
        data-testid="portfolio-project-tasks-dialog"
      >
        <DialogHeader className="px-3 sm:px-6 py-3 border-b border-border flex-shrink-0">
          <DialogTitle className="text-sm sm:text-base flex items-center gap-2">
            <span className="label-eyebrow text-[10px]">Project tasks</span>
            <span className="truncate">{project.name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto" data-testid="portfolio-project-tasks-body">
          <Tasks embedded initialProjectIds={[project.id]} />
        </div>
      </DialogContent>
    </Dialog>
  );
}


// R138u · Projects tab rebuilt on top of the shared <AdvancedTable>.
// Gives directors saved views, per-column sort/filter, sticky headers,
// CSV/XLSX/PDF export, group-by, and grand totals — the same power the
// rest of the app enjoys. Row-level quick-actions (peek, add task, post
// update, open project) are preserved via the render function of the
// "Actions" column.
// R138x · Feb 2026 — Row click OR the "peek" icon opens a full-height
// dialog that mounts `<Tasks embedded initialProjectIds=[...]>` — the
// entire Tasks powerhouse (list · cards · kanban · calendar · bulk
// edit · quick add) scoped to that one project. Same UX as Task
// Intelligence's PersonTasksDialog — no more read-only peek.
function ProjectsAdvancedTable({ portfolioId, projects, onCommentPosted }) {
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [commentTarget, setCommentTarget] = useState(null);
  const [projectTasksDrill, setProjectTasksDrill] = useState(null);
  const [quickAddProject, setQuickAddProject] = useState(null);
  const [updatesProject, setUpdatesProject] = useState(null);

  const columns = useMemo(() => [
    {
      key: "name", label: "Project", width: 220, sortable: true,
      accessor: (p) => p.name || "",
      render: (p) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); setProjectTasksDrill({ id: p.id, name: p.name }); }}
                  className="text-left font-medium truncate hover:underline"
                  title="Show open tasks for this project"
                  data-testid={`pf-tbl-name-${p.id}`}>{p.name}</button>
      ),
    },
    { key: "client_name", label: "Client", width: 150, sortable: true,
      accessor: (p) => p.client_name || "",
      filterType: "text" },
    { key: "brand_name", label: "Brand", width: 150, sortable: true,
      accessor: (p) => p.brand_name || "",
      filterType: "text" },
    {
      key: "health", label: "Health", width: 100, sortable: true,
      accessor: (p) => p.health || "unset",
      filterType: "select",
      filterOptions: Object.keys(HEALTH_LABEL).map((k) => ({ value: k, label: HEALTH_LABEL[k] })),
      render: (p) => (
        <span className={`text-[10px] px-2 py-0.5 border ${HEALTH_COLOR[p.health || "unset"]}`}>
          {HEALTH_LABEL[p.health || "unset"]}
        </span>
      ),
    },
    {
      key: "status", label: "Status", width: 110, sortable: true,
      accessor: (p) => p.status || "",
      filterType: "text",
    },
    {
      key: "start_date", label: "Start", width: 100, sortable: true, align: "left",
      accessor: (p) => p.start_date || "",
      exportValue: (p) => p.start_date || "",
    },
    {
      key: "due_date", label: "Due", width: 100, sortable: true, align: "left",
      accessor: (p) => p.due_date || "",
      exportValue: (p) => p.due_date || "",
      render: (p) => {
        const overdue = p.due_date && p.status !== "done" &&
          new Date(p.due_date) < new Date(new Date().toDateString());
        return (
          <span className={`text-[11px] tabular-nums ${overdue ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>
            {p.due_date || "—"}
          </span>
        );
      },
    },
    {
      key: "total_tasks", label: "Tasks", width: 70, sortable: true,
      align: "right", type: "number", sum: true,
      accessor: (p) => p._stats?.total_tasks || 0,
      exportValue: (p) => p._stats?.total_tasks || 0,
    },
    {
      key: "completion_pct", label: "Complete %", width: 100, sortable: true,
      align: "right", type: "number",
      accessor: (p) => p._stats?.completion_pct || 0,
      exportValue: (p) => p._stats?.completion_pct || 0,
      render: (p) => `${p._stats?.completion_pct || 0}%`,
    },
    {
      key: "overdue_tasks", label: "Overdue", width: 90, sortable: true,
      align: "right", type: "number", sum: true,
      accessor: (p) => p._stats?.overdue_tasks || 0,
      exportValue: (p) => p._stats?.overdue_tasks || 0,
      render: (p) => (
        p._stats?.overdue_tasks
          ? <span className="text-rose-700 font-bold">{p._stats.overdue_tasks}</span>
          : <span className="text-muted-foreground">0</span>
      ),
    },
    {
      key: "budget", label: "Budget", width: 110, sortable: true,
      align: "right", type: "currency", visible: false,
      // R138w · Backend rollup returns `budget`, not `budget_amount`.
      accessor: (p) => p.budget || p.budget_amount || 0,
      exportValue: (p) => p.budget || p.budget_amount || 0,
      render: (p) => {
        const v = p.budget || p.budget_amount || 0;
        return v ? v.toLocaleString() : "—";
      },
    },
    {
      key: "invoiced", label: "Invoiced", width: 110, sortable: true,
      align: "right", type: "currency", visible: false,
      accessor: (p) => p._stats?.invoiced_amount || 0,
    },
    {
      key: "actions", label: "Actions", width: 180, sortable: false,
      align: "right",
      render: (p) => (
        <div className="flex items-center justify-end gap-0.5">
          <button onClick={(e) => { e.stopPropagation(); setQuickAddProject({ id: p.id, name: p.name, at: Date.now() }); }}
                    className="p-1 hover:bg-primary/10 text-primary"
                    title="Add task"
                    data-testid={`pf-tbl-add-${p.id}`}><Plus className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => { e.stopPropagation(); setProjectTasksDrill({ id: p.id, name: p.name }); }}
                    className="p-1 hover:bg-muted"
                    title="Show open tasks for this project"
                    data-testid={`pf-tbl-peek-${p.id}`}><ListTodo className="w-3.5 h-3.5 text-muted-foreground" /></button>
          <button onClick={(e) => { e.stopPropagation(); setUpdatesProject({ id: p.id, name: p.name }); }}
                    className="p-1 hover:bg-muted"
                    title="Recent updates"
                    data-testid={`pf-tbl-updates-${p.id}`}><Activity className="w-3.5 h-3.5 text-muted-foreground" /></button>
          <button onClick={(e) => { e.stopPropagation(); setCommentTarget(p); }}
                    className="p-1 hover:bg-muted"
                    title="Post quick comment"
                    data-testid={`pf-tbl-comment-${p.id}`}><MessageSquarePlus className="w-3.5 h-3.5 text-sky-600" /></button>
          <button onClick={() => nav(`/app/projects/${p.id}`)}
                    className="p-1 hover:bg-muted"
                    title="Open project"
                    data-testid={`pf-tbl-open-${p.id}`}><ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground" /></button>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <div data-testid="portfolio-projects-adv-table">
      <AdvancedTable
        tableKey={`portfolio-projects:${portfolioId}`}
        title={`Projects · ${projects.length}`}
        rows={projects || []}
        columns={columns}
        emptyTitle="No projects yet"
        emptyDescription="Add projects or clients to this portfolio from the Config panel."
        includeAuditColumns={false}
      />
      <QuickCommentDialog
        portfolioId={portfolioId}
        project={commentTarget}
        onClose={() => setCommentTarget(null)}
        onPosted={onCommentPosted}
      />
      {/* R138x · Full Tasks powerhouse scoped to the clicked project. */}
      {projectTasksDrill && (
        <ProjectTasksDrillDialog
          project={projectTasksDrill}
          onClose={() => setProjectTasksDrill(null)}
        />
      )}
      <ProjectUpdatesPeekDialog
        open={!!updatesProject}
        onOpenChange={(o) => !o && setUpdatesProject(null)}
        projectId={updatesProject?.id}
        projectName={updatesProject?.name}
      />
      {quickAddProject && (
        <QuickAddTaskPopover
          key={quickAddProject.at}
          wsId={activeWorkspaceId}
          preset={{ project_id: quickAddProject.id }}
          defaultOpen
          onOpenChange={(v) => { if (!v) setQuickAddProject(null); }}
          onCreated={() => { setQuickAddProject(null); onCommentPosted?.(); }}
        />
      )}
    </div>
  );
}


function FinCell({ label, value, tone }) {
  const toneClass = tone === "rose" ? "text-rose-700" : "";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </div>
      <div className={`text-lg font-bold tabular-nums ${toneClass}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}



